import { useState, useEffect } from "react";
import { useGameStore, type Research } from "../store/gameStore";
import { useUIStore } from "../store/uiStore";
import { useApi } from "../hooks/useApi";
import { hapticFeedback } from "../telegram/init";
import { ResearchPanel } from "../components/panels/ResearchPanel";
import { RESEARCH_DEFINITIONS, ResearchType, ResearchBranch } from "@ikarium/shared";
import { Timer } from "../components/shared/Timer";

// ─── Static tree order (DFS pre-order, depth = indent level) ───────────────

interface TreeNode {
  type: ResearchType;
  depth: number;
}

const ECONOMY_TREE: TreeNode[] = [
  { type: ResearchType.CONSERVATION, depth: 0 },
  { type: ResearchType.PULLEY,       depth: 1 },
  { type: ResearchType.CARTOGRAPHY,  depth: 2 },
  { type: ResearchType.TAXATION,     depth: 1 },
  { type: ResearchType.IRRIGATION,   depth: 2 },
];

const MILITARY_TREE: TreeNode[] = [
  { type: ResearchType.BRONZE_ARMOR,      depth: 0 },
  { type: ResearchType.SHARPENED_BLADES,  depth: 1 },
  { type: ResearchType.FORMATIONS,        depth: 2 },
  { type: ResearchType.SIEGE_CRAFT,       depth: 3 },
  { type: ResearchType.NAVAL_WARFARE,     depth: 1 },
];

// ─── Research icons ─────────────────────────────────────────────────────────

const RESEARCH_ICONS: Record<string, string> = {
  CONSERVATION:     "\u267B\uFE0F",
  PULLEY:           "\u2699\uFE0F",
  CARTOGRAPHY:      "\u{1F5FA}\uFE0F",
  TAXATION:         "\u{1F4B0}",
  IRRIGATION:       "\u{1F4A7}",
  BRONZE_ARMOR:     "\u{1F6E1}\uFE0F",
  SHARPENED_BLADES: "\u2694\uFE0F",
  FORMATIONS:       "\u{1F3DB}\uFE0F",
  SIEGE_CRAFT:      "\u{1F4A3}",
  NAVAL_WARFARE:    "\u2693",
};

// ─── Helper: get research record or default ──────────────────────────────────

function getResearch(type: string, researches: Research[]): Research {
  return researches.find((r) => r.researchType === type) ?? {
    researchType: type,
    level: 0,
    isResearching: false,
    researchEndAt: null,
  };
}

type NodeState = "locked" | "available" | "in-progress" | "maxed";

function getNodeState(type: ResearchType, researches: Research[]): NodeState {
  const r = getResearch(type, researches);
  const def = RESEARCH_DEFINITIONS[type];

  if (r.isResearching) return "in-progress";
  if (r.level >= def.maxLevel) return "maxed";

  if (def.prerequisite) {
    const prereq = getResearch(def.prerequisite.type, researches);
    if (prereq.level < def.prerequisite.level) return "locked";
  }

  return "available";
}

// ─── Main screen ─────────────────────────────────────────────────────────────

export function ResearchScreen() {
  const researches    = useGameStore((s) => s.researches);
  const user          = useGameStore((s) => s.user);
  const setResearches = useGameStore((s) => s.setResearches);
  const activePanel   = useUIStore((s) => s.activePanel);
  const openResearchPanel = useUIStore((s) => s.openResearchPanel);
  const api           = useApi();

  const [branch, setBranch] = useState<"ECONOMY" | "MILITARY">("ECONOMY");

  const tree = branch === "ECONOMY" ? ECONOMY_TREE : MILITARY_TREE;
  const anyResearching = researches.some((r) => r.isResearching);

  // Poll every 5s while something is in-progress
  useEffect(() => {
    if (!anyResearching) return;
    const interval = setInterval(async () => {
      try {
        const data = await api.getResearches();
        setResearches(data.researches);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [anyResearching]);

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    }}>
      {/* ── Header ── */}
      <div className="meander-border" style={{
        padding: "var(--space-md) var(--space-lg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "linear-gradient(135deg, rgba(201,169,97,0.08), transparent)",
      }}>
        <div>
          <h2 style={{
            fontFamily: "var(--font-display)",
            fontSize: "1.1rem",
            color: "var(--ink)",
          }}>
            Research
          </h2>
          <span style={{ fontSize: "0.75rem", color: "var(--ink-faded)" }}>
            Academy of Sciences
          </span>
        </div>
        {user && (
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "6px 12px",
            borderRadius: "20px",
            background: "var(--parchment)",
            border: "1px solid var(--sand)",
          }}>
            <span style={{ fontSize: "0.85rem" }}>{"\u{1F4DA}"}</span>
            <span style={{
              fontFamily: "var(--font-display)",
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "var(--ink-light)",
            }}>
              {Math.floor(user.researchPoints).toLocaleString()} RP
            </span>
          </div>
        )}
      </div>

      {/* ── Branch tabs ── */}
      <div style={{
        display: "flex",
        gap: "var(--space-sm)",
        padding: "var(--space-md) var(--space-lg)",
        borderBottom: "1px solid var(--sand)",
        background: "var(--papyrus-light)",
      }}>
        {(["ECONOMY", "MILITARY"] as const).map((b) => (
          <button
            key={b}
            onClick={() => { hapticFeedback("light"); setBranch(b); }}
            style={{
              flex: 1,
              padding: "8px 0",
              borderRadius: "20px",
              border: branch === b
                ? "1.5px solid var(--gold)"
                : "1.5px solid var(--sand)",
              background: branch === b
                ? "linear-gradient(135deg, rgba(201,169,97,0.22), rgba(218,183,82,0.12))"
                : "transparent",
              fontFamily: "var(--font-display)",
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.05em",
              color: branch === b ? "var(--gold-dim)" : "var(--ink-faded)",
              textTransform: "uppercase",
            }}
          >
            {b === "ECONOMY" ? "\u{1F3DB}\uFE0F Economy" : "\u2694\uFE0F Military"}
          </button>
        ))}
      </div>

      {/* ── Research tree ── */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "var(--space-lg)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-sm)",
      }}>
        {tree.map((node, i) => (
          <ResearchNode
            key={node.type}
            type={node.type}
            depth={node.depth}
            state={getNodeState(node.type, researches)}
            research={getResearch(node.type, researches)}
            index={i}
            onTap={() => {
              hapticFeedback("medium");
              openResearchPanel(node.type);
            }}
          />
        ))}
        <div style={{ height: "var(--space-xl)" }} />
      </div>

      {/* ── Panel ── */}
      {activePanel === "research" && <ResearchPanel />}
    </div>
  );
}

// ─── Single research node card ───────────────────────────────────────────────

interface ResearchNodeProps {
  type: ResearchType;
  depth: number;
  state: NodeState;
  research: Research;
  index: number;
  onTap: () => void;
}

function ResearchNode({ type, depth, state, research, index, onTap }: ResearchNodeProps) {
  const def  = RESEARCH_DEFINITIONS[type];
  const icon = RESEARCH_ICONS[type] ?? "\u{1F4DC}";
  const INDENT = 20;

  const isLocked     = state === "locked";
  const isInProgress = state === "in-progress";
  const isMaxed      = state === "maxed";
  const isAvailable  = state === "available";

  const borderColor = isOwn(state) ? "var(--gold)" :
    isLocked    ? "var(--sand)" :
    isMaxed     ? "var(--olive-light)" :
    isInProgress ? "var(--gold)" :
    "var(--sand)";

  const bgColor = isMaxed
    ? "linear-gradient(135deg, rgba(91,117,83,0.1), rgba(122,154,111,0.06))"
    : isInProgress
      ? "linear-gradient(135deg, rgba(201,169,97,0.12), rgba(218,183,82,0.06))"
      : "linear-gradient(135deg, var(--marble), var(--papyrus-light))";

  return (
    <div style={{
      display: "flex",
      alignItems: "stretch",
      animation: `stagger-in 0.3s ease both ${index * 50}ms`,
    }}>
      {/* Tree indent lines */}
      {Array.from({ length: depth }, (_, d) => (
        <div key={d} style={{
          width: `${INDENT}px`,
          flexShrink: 0,
          borderRight: d === depth - 1 ? "2px solid var(--sand)" : "none",
          marginRight: d === depth - 1 ? "var(--space-sm)" : 0,
          opacity: 0.4,
        }} />
      ))}

      {/* Card */}
      <button
        onClick={onTap}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          gap: "var(--space-md)",
          padding: "var(--space-md)",
          borderRadius: "var(--radius-lg)",
          border: `1.5px solid ${borderColor}`,
          background: bgColor,
          boxShadow: isAvailable || isInProgress ? "var(--shadow-soft)" : "none",
          opacity: isLocked ? 0.5 : 1,
          textAlign: "left",
          cursor: "pointer",
          position: "relative",
          overflow: "hidden",
          animation: isInProgress ? "gold-glow 2.5s ease infinite" : undefined,
        }}
      >
        {/* Icon */}
        <div style={{
          width: "40px",
          height: "40px",
          borderRadius: "var(--radius)",
          background: isLocked
            ? "rgba(122,104,85,0.08)"
            : "linear-gradient(135deg, var(--parchment), var(--papyrus-dark))",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.3rem",
          flexShrink: 0,
          filter: isLocked ? "grayscale(0.8)" : undefined,
        }}>
          {icon}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: "var(--font-display)",
            fontSize: "0.82rem",
            fontWeight: 700,
            color: isLocked ? "var(--ink-faded)" : "var(--ink)",
            letterSpacing: "0.02em",
            marginBottom: "2px",
          }}>
            {def.name}
          </div>
          <div style={{
            fontSize: "0.7rem",
            color: "var(--ink-faded)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {def.effect}
          </div>
          {isInProgress && research.researchEndAt && (
            <div style={{ marginTop: "3px" }}>
              <Timer endAt={research.researchEndAt} compact />
            </div>
          )}
        </div>

        {/* Right badge */}
        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
          {/* Level badge */}
          <span style={{
            background: isMaxed
              ? "var(--olive)"
              : isInProgress
                ? "var(--gold-dim)"
                : "var(--terra)",
            color: "var(--papyrus-light)",
            fontFamily: "var(--font-display)",
            fontSize: "0.6rem",
            fontWeight: 700,
            padding: "2px 8px",
            borderRadius: "10px",
          }}>
            {isMaxed ? "MAX" : `Lv ${research.level}/${def.maxLevel}`}
          </span>

          {/* State icon */}
          {isLocked    && <span style={{ fontSize: "0.85rem", opacity: 0.5 }}>{"\u{1F512}"}</span>}
          {isMaxed     && <span style={{ fontSize: "0.85rem" }}>{"\u2705"}</span>}
          {isInProgress && <span style={{ fontSize: "0.85rem", animation: "pulse 1.5s infinite" }}>{"\u{1F52C}"}</span>}
        </div>
      </button>
    </div>
  );
}

function isOwn(_state: NodeState): boolean {
  return false; // placeholder — not used but satisfies TS
}
